import { Code, ConnectError } from '@connectrpc/connect';

// Response headers set by the backend (backend/internal/infrastructure/observability/headers.go).
// They are only readable cross-origin because the API lists them in CORS ExposedHeaders.
export const REQUEST_ID_HEADER = 'X-Request-ID';
export const ERROR_CODE_HEADER = 'X-A6TR-Error-Code';

// Codes that reflect a normal business outcome or a user action rather than a defect, so they
// are not reported to Sentry.
const EXPECTED_CODES: ReadonlySet<Code> = new Set([
	Code.Canceled,
	Code.NotFound,
	Code.InvalidArgument,
	Code.FailedPrecondition,
	Code.AlreadyExists,
	Code.OutOfRange,
	Code.PermissionDenied,
	Code.Unauthenticated,
	Code.ResourceExhausted,
]);

export type ApiErrorDetails = {
	connectError: ConnectError;
	code: Code;
	codeName: string;
	requestId?: string;
	errorCode?: string;
};

export function getApiErrorDetails(error: unknown): ApiErrorDetails {
	const connectError = ConnectError.from(error);
	return {
		connectError,
		code: connectError.code,
		codeName: Code[connectError.code] ?? String(connectError.code),
		requestId: firstHeaderValue(connectError.metadata.get(REQUEST_ID_HEADER)),
		errorCode: connectError.metadata.get(ERROR_CODE_HEADER) ?? undefined,
	};
}

// connect-go appends error metadata to the headers already set by the HTTP middleware, so the
// request ID can arrive twice and Headers.get() joins the values with a comma.
function firstHeaderValue(value: string | null): string | undefined {
	return value?.split(',')[0]?.trim() || undefined;
}

export function isExpectedApiError(code: Code): boolean {
	return EXPECTED_CODES.has(code);
}

// Messages browsers give the TypeError fetch rejects with when no response was received (offline,
// DNS, connection reset, CORS refusal): Chromium, Firefox, Safari. The Sentry SDK appends the
// request host in parentheses, hence the prefix match.
const NETWORK_ERROR_MESSAGE =
	/^(Failed to fetch|NetworkError when attempting to fetch resource|Load failed)/;

// True when the request never got an HTTP response. The browser hides the reason from scripts, so
// there is nothing to debug from the client side.
export function isNetworkError(error: unknown): boolean {
	const connectError = ConnectError.from(error);
	const cause: unknown = connectError.cause;
	return (
		connectError.code === Code.Unknown &&
		cause instanceof TypeError &&
		NETWORK_ERROR_MESSAGE.test(cause.message)
	);
}
